import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useBooking } from "@/context/BookingContext";
import { checkoutStatus } from "@/lib/api";

const MAX_POLLS = 8;

export default function Confirmation() {
  const [sp] = useSearchParams();
  const { reset } = useBooking();
  const sessionId = sp.get("session_id") || "";
  const ref = sp.get("ref") || "";
  const email = sp.get("email") || "";
  const [state, setState] = useState(sessionId ? "checking" : "paid");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) { reset(); return; }
    let tries = 0, timer;
    const poll = async () => {
      tries += 1;
      try {
        const r = await checkoutStatus(sessionId);
        if (r.payment_status === "paid") { setAmount(r.amount); setState("paid"); reset(); return; }
        if (r.status === "expired") { setState("expired"); return; }
      } catch { /* keep polling */ }
      if (tries >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="pp-container py-20 sm:py-28">
        <div className="max-w-2xl mx-auto text-center pp-fade-up" data-testid="confirmation-page" data-state={state}>
          {state === "checking" && (
            <>
              <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--pp-terracotta)" }} />
              <div className="pp-caption mt-6" data-testid="payment-checking">Confirming your payment…</div>
              <p className="mt-3 text-sm" style={{ color: "var(--pp-text-muted)" }}>This only takes a moment. Please don't close the page.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: "#3F5B33" }} />
              <div className="pp-caption mt-6 mb-4">Paid &amp; booked</div>
              <h1 className="font-serif-display text-5xl sm:text-6xl font-light" style={{ color: "var(--pp-burgundy)" }} data-testid="payment-success">
                Your porch is booked.
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
                {amount != null && <>We've received your payment of <span className="font-medium" style={{ color: "var(--pp-text)" }}>${Number(amount).toLocaleString()}</span>. </>}
                A confirmation is on its way to <span className="font-medium" style={{ color: "var(--pp-text)" }}>{email}</span>, and we'll be in touch before your install.
              </p>
              <div className="mt-10 inline-block border px-10 py-6" style={{ borderColor: "var(--pp-burgundy)" }} data-testid="confirmation-ref">
                <div className="pp-caption mb-1">Your reference</div>
                <div className="font-serif-display text-4xl tracking-widest" style={{ color: "var(--pp-terracotta)" }}>{ref}</div>
              </div>
              <p className="mt-6 text-xs" style={{ color: "var(--pp-text-muted)" }}>A note on changes: any change to colors, pieces or package after booking is treated as an addition and carries an extra charge.</p>
            </>
          )}
          {(state === "expired" || state === "timeout") && (
            <>
              <AlertTriangle className="h-10 w-10 mx-auto" style={{ color: "#8A5A20" }} />
              <h1 className="font-serif-display text-4xl sm:text-5xl font-light mt-6" style={{ color: "var(--pp-burgundy)" }} data-testid="payment-pending">
                {state === "expired" ? "That checkout session expired." : "We're still waiting on your payment."}
              </h1>
              <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
                {state === "expired" ? "No charge was made. Head back and try checkout again whenever you're ready." : "If you completed payment, your confirmation email will arrive shortly and your reservation page will update. Reference: "}
                {state !== "expired" && <span className="font-medium" style={{ color: "var(--pp-text)" }}>{ref}</span>}
              </p>
            </>
          )}
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to={`/lookup?email=${encodeURIComponent(email)}&reference=${encodeURIComponent(ref)}`} data-testid="confirmation-lookup-link" className="pp-btn">
              View my reservation
            </Link>
            <Link to="/" data-testid="confirmation-home-link" className="pp-btn-ghost self-center" style={{ color: "var(--pp-text-muted)" }}>Back to home</Link>
          </div>
        </div>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}
